import i18next from 'i18next';

// The catalog list renders both an unset category and the client unnamed category bucket as
// "Aucune", so the filter merges them into a single choice carrying a null id — the catalog route
// reads that null as "no category" instead of looking an id up.
const NONE_CHOICE_ID = 'none';

/**
 * Build the choices of a category / sub-category filter. Unnamed categories are dropped from the
 * named choices and folded into the trailing "Aucune" one, which the list column already uses for
 * every product without a readable category.
 *
 * @param {Array<Object>} items - The client categories or sub-categories ({ id, name })
 * @param {string} propertyKey - The request body key the choice value is read from
 *
 * @returns {Array<Object>} The filter choices
 */
const getCategoryChoices = (items, propertyKey) => {
  const namedChoices = items
    .filter(({ name }) => !!name)
    .map(({ id, name }) => ({
      id,
      name,
      [propertyKey]: id,
    }));

  return [
    ...namedChoices,
    {
      id: NONE_CHOICE_ID,
      name: i18next.t('GENERAL.NONE_VALUE'),
      [propertyKey]: null,
    },
  ];
};

/**
 * Filter entries fed to the DeepsightFiltersButton of the catalog list. They are declared apart from
 * the ListView columns because both overload `propertyKey` for different purposes: ListView sorts on
 * it (`category`), while the filters turn it into the request body key expected by
 * POST /v2/b2b/external-clients/:id/catalog (`categoryId`). Each choice therefore carries a property
 * named after the filter key holding the raw value, which `getQueryParamOperation` maps to an
 * `{ inArray: [...] }` condition.
 *
 * @param {Array<Object>} categories - The client product categories ({ id, name })
 * @param {Array<Object>} subCategories - The client product sub-categories ({ id, name })
 * @param {boolean} hasCentralKitchens - Whether the central-product filter is relevant
 *
 * @returns {Array<Object>} The columnsFilterList entries
 */
export const getCatalogFilterList = (categories, subCategories, hasCentralKitchens) => {
  const filterList = [];

  if (categories.length) {
    filterList.push({
      id: 'categoryId',
      propertyKey: 'categoryId',
      name: i18next.t('GENERAL.CATEGORY'),
      filterType: 'string',
      list: getCategoryChoices(categories, 'categoryId'),
    });
  }

  if (subCategories.length) {
    filterList.push({
      id: 'subCategoryId',
      propertyKey: 'subCategoryId',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
      filterType: 'string',
      list: getCategoryChoices(subCategories, 'subCategoryId'),
    });
  }

  if (hasCentralKitchens) {
    filterList.push({
      id: 'isKitchen',
      propertyKey: 'isKitchen',
      name: i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_CENTRAL_PRODUCT'),
      filterType: 'string',
      list: [
        { id: 'central', name: i18next.t('GENERAL.YES'), isKitchen: true },
        { id: 'non-central', name: i18next.t('GENERAL.NO'), isKitchen: false },
      ],
    });
  }

  return filterList;
};
